import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import './App.css';
import { getApiUrl } from './config/api';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProjectList from './components/ProjectList';
import CustomerList from './components/CustomerList';
import SupplierList from './components/SupplierList';
import HostingDomainList from './components/HostingDomainList';
import SEODashboard from './components/SEODashboard';
import ContractList from './components/ContractList';
import UserManagement from './components/UserManagement';
import RoleManagement from './components/RoleManagement';
import PermissionList from './components/PermissionList';
import TaskList from './components/TaskList';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import TransactionList from './components/TransactionList';

// Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Remove axios default baseURL since we use getApiUrl
// axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Map permission code to resource/action for Sidebar compatibility
  const normalizePermissions = (roles) => {
    return roles.map(role => ({
      ...role,
      permissions: role.permissions?.map(p => {
        if (p.code === 'view_financials') return { resource: 'finance', action: 'read' };
        if (p.code === 'manage_financials') return { resource: 'finance', action: 'write' };
        if (p.code === 'delete_financials') return { resource: 'finance', action: 'delete' };
        // add more mappings if needed
        return p;
      })
    }));
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/auth/me'));
      setUser({
        ...response.data,
        roles: normalizePermissions(response.data.roles)
      });
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [token, fetchUserInfo]);

  const login = async (email, password) => {
    try {
      console.log('Attempting login:', { email, password: '***' });
      const response = await axios.post(getApiUrl('/auth/login'), { email, password });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      console.log('Login successful');
      return true;
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);
      return false;
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const hasPermission = (resource, action) => {
    if (!user || !user.roles) return false;
    
    // Super admin has all permissions
    if (user.roles.some(role => role.name === 'super_admin')) {
      return true;
    }

    // Check specific permissions - handle case where permissions might not be loaded
    return user.roles.some(role => 
      role.permissions?.some(permission => 
        permission.resource === resource && 
        (permission.action === action || permission.action === 'write')
      )
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  const authValue = {
    user,
    login,
    logout,
    updateUser,
    hasPermission,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={authValue}>
      <Router>
        <div className="min-h-screen bg-gray-50">
          {user ? (
            <div className="flex">
              <Sidebar />
              <div className="flex-1">
                <Navbar />
                <main className="p-6">
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/projects" element={<ProjectList />} />
                    <Route path="/customers" element={<CustomerList />} />
                    <Route path="/suppliers" element={<SupplierList />} />
                    <Route path="/hosting-domains" element={<HostingDomainList />} />
                    <Route path="/seo" element={<SEODashboard />} />
                    <Route path="/contracts" element={<ContractList />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/roles" element={<RoleManagement />} />
                    <Route path="/permissions" element={<PermissionList />} />
                    <Route path="/tasks" element={<TaskList />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/transactions" element={<TransactionList />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
              </div>
            </div>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </div>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#22c55e',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
